// Main entry point - imports all main JS files in order
// Ensure userData is always defined as an object
window.userData = window.userData || {};
import './safe-bootstrap.js';
import './post-composer.js';
// Explicit vendor imports (no reliance on global script tags)
import jQuery from 'jquery';
import underscore from 'underscore';
import BackboneLib from 'backbone';

// Bind vendors for legacy modules that expect globals
const globalJQuery =
  typeof window.jQuery === 'function' && window.jQuery.fn ? window.jQuery : jQuery;
window.$ = globalJQuery;
window.jQuery = globalJQuery;
window._ = underscore;
window.Backbone = BackboneLib;
BackboneLib.$ = globalJQuery;
const __SKIP_DYNAMIC_IMPORTS__ =
  (typeof __BUNDLED__ !== 'undefined' && __BUNDLED__ === true) ||
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.BUNDLED === 'true');
// Use globals since these are loaded via script tags in templates
let _ = window._;
let Backbone = window.Backbone;

// Ensure globals are set from loaded scripts
window._ = _;
window.Backbone = Backbone;

async function ensureVendorsLoaded() {
  // jQuery - wait for it to be loaded via script tag
  let retries = 0;
  while (
    typeof window.jQuery === 'undefined' &&
    typeof window.$ === 'undefined' &&
    retries < 100
  ) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    retries++;
  }
  if (typeof window.jQuery === 'undefined' && typeof window.$ === 'undefined') {
    // Try dynamic import first
    try {
      const jq = await import('jquery');
      const jref = jq.default || jq;
      window.jQuery = jref;
      window.$ = jref;
    } catch (e) {
      // As a last resort, inject CDN script
      await new Promise((resolve) => {
        try {
          const script = document.createElement('script');
          script.src =
            String(window.STATIC_URL || '/static').replace(/\/$/, '') +
            '/libs/jquery-3.5.1.min.js';
          script.onload = resolve;
          script.onerror = resolve;
          document.head.appendChild(script);
        } catch (_) {
          resolve();
        }
      });
    }
  }

  // Additional wait to ensure jQuery is fully initialized
  if (typeof window.jQuery !== 'undefined' || typeof window.$ !== 'undefined') {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  // Ensure Underscore
  if (typeof window._ === 'undefined') {
    try {
      const modUnderscore = await import('underscore');
      window._ = modUnderscore.default || modUnderscore;
    } catch (e) {
      // ignore
    }
  }

  // Ensure Backbone
  if (typeof window.Backbone === 'undefined') {
    try {
      const modBackbone = await import('backbone');
      window.Backbone = modBackbone.default || modBackbone;
    } catch (e) {
      // ignore
    }
  }

  // Wire Backbone to jQuery if available
  if (
    (typeof window.jQuery !== 'undefined' || typeof window.$ !== 'undefined') &&
    window.Backbone &&
    window.Backbone.$ !== (window.jQuery || window.$)
  ) {
    window.Backbone.$ = window.jQuery || window.$;
  }

  // Shepherd - wait for it to be loaded via script tag if needed
  if (typeof window.Shepherd === 'undefined') {
    try {
      const mod = await import('shepherd.js');
      window.Shepherd = mod.default || mod;
    } catch (e) {
      // optional; some pages do not need Shepherd
    }
  }
}

// Load the rest of the app after globals are set
(async () => {
  const vendorsPromise = ensureVendorsLoaded();
  window.vendorsReadyPromise = vendorsPromise;
  window.ensureVendorsReady = ensureVendorsLoaded;
  await vendorsPromise;

  // Load foundational modules (no runtime dynamic imports when bundled)
  if (!__SKIP_DYNAMIC_IMPORTS__) {
    await (window.safeImport
      ? window.safeImport('./data-accessor.js')
      : import('./data-accessor.js'));
    await (window.safeImport
      ? window.safeImport('./views/views.js')
      : import('./views/views.js'));
  }
  window.viewsLoaded = true;
  // Skip bootstrap.min.js here - it's loaded via script tag in templates for production
  await import('./shared.js');
  await import('./embedded-checkout.js');
  await import('./session-manager.js');
  await import('./api-helper.js');
  await import('./auth-modal.js');
  await import('./signup.js');
  // Vendor UMD libraries are loaded via classic <script> tags in templates
  // to avoid module execution context issues. Do not import them here.
  if (!__SKIP_DYNAMIC_IMPORTS__) {
    await (window.safeImport
      ? window.safeImport('./word-frequency-loader.js')
      : import('./word-frequency-loader.js'));
    await (window.safeImport
      ? window.safeImport('./autocomplete.js')
      : import('./autocomplete.js'));
    await (window.safeImport
      ? window.safeImport('./ai-chatbots-enhancements.js')
      : import('./ai-chatbots-enhancements.js'));
    await (window.safeImport
      ? window.safeImport('./string-utils.js')
      : import('./string-utils.js'));
    await (window.safeImport
      ? window.safeImport('./cookie-utils.js')
      : import('./cookie-utils.js'));
    await (window.safeImport
      ? window.safeImport('./dom-utils.js')
      : import('./dom-utils.js'));
    await (window.safeImport
      ? window.safeImport('./image-utils.js')
      : import('./image-utils.js'));
    await (window.safeImport
      ? window.safeImport('./navigation-utils.js')
      : import('./navigation-utils.js'));
    await (window.safeImport
      ? window.safeImport('./navigation-manager-loader.js')
      : import('./navigation-manager-loader.js'));
    await (window.safeImport
      ? window.safeImport('./collection-utils.js')
      : import('./collection-utils.js'));
    await (window.safeImport
      ? window.safeImport('./template-utils.js')
      : import('./template-utils.js'));
    await (window.safeImport
      ? window.safeImport('./auth-utils.js')
      : import('./auth-utils.js'));
    await (window.safeImport
      ? window.safeImport('./background-utils.js')
      : import('./background-utils.js'));
    await (window.safeImport
      ? window.safeImport('./eventsource-manager.js')
      : import('./eventsource-manager.js'));
    await (window.safeImport
      ? window.safeImport('./chat-mode-switcher.js')
      : import('./chat-mode-switcher.js'));
    await (window.safeImport
      ? window.safeImport('./game.js')
      : import('./game.js'));
  }

  // Ensure signup initialization happens after all modules are loaded
  if (window.initSignup && typeof window.initSignup === 'function') {
    window.initSignup();
  } else {
    setTimeout(() => {
      if (window.initSignup && typeof window.initSignup === 'function') {
        window.initSignup();
      }
    }, 100);
  }
})();
